import React, { useEffect, useState } from 'react';
import { useWowLayer, lookupCategoryColor } from '../wow/WowLayer';
import type { CategoryID } from '../wow/types';
import type { Accessor, Color } from '@deck.gl/core';
import { useAtom, useAtomValue } from 'jotai';
import { loadable } from '../utils/loadable';
import { visualModeAtom } from '../map/atoms';
import { wowChunksSelector, usePeriodicWowDataRefresh } from '../wow/selectors';
import bbox from '@turf/bbox';
import centerOfMass from '@turf/center-of-mass';
import type { Feature, FeatureCollection, GeoJsonProperties } from 'geojson';
import { ReactComponent as TempoLogo } from '../images/tempo_full_color_DKGray.svg';
import Box from '@mui/material/Box';
import StoryList from '../wowcast/StoryList';
import { Link } from 'react-router-dom';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { StyleSpecification } from 'maplibre-gl';
import { Map as MaplibreMap } from 'react-map-gl/maplibre';
import DeckGL from '@deck.gl/react';
import { MapView, FlyToInterpolator } from '@deck.gl/core';
import { GeoJsonLayer } from '@deck.gl/layers';
import type { InitialViewStateProps, ViewStateProps } from '../map/deckgl-types';
import { buildRasterStyle, getBasemap, mapStyleAtom } from '../map/basemaps';
import { suppressMapContextMenu } from '../map/deckWrapper';
import { useRegionLayer } from '../Regions/FemaRegions';
import { ToggleLabelsButton } from '../map/ToggleLabelsButton';
import IconButton from '@mui/material/IconButton';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { useAnimationLogic } from '../utils/useAnimationLogic';
import { PostLegend } from '../Post/PostLayer';
import { Typography } from '@mui/material';
import { fetchBasemapStyle, transformProxyRequest } from '../services/arcgisProxy';

export interface FlightPoint {
  latitude: number;
  longitude: number;
  zoom: number;
  properties: GeoJsonProperties;
  bbox?: [number, number, number, number];
}

const calculateZoomLevel = (minLng: number, maxLng: number): number => {
  const baseZoomLevel = Math.floor(Math.log2(360 / (maxLng - minLng)));
  const adjustedZoomLevel = baseZoomLevel;
  // Optionally, you can clamp the zoom level to ensure it stays within a desired range
  return adjustedZoomLevel;
};

const createFlightPathFromIndividualFeatures = (
  featureCollection: FeatureCollection
): FlightPoint[] => {
  const flightPath: FlightPoint[] = [];

  featureCollection.features.forEach((feature: Feature) => {
    const [minLng, minLat, maxLng, maxLat] = bbox(feature);
    const featureCenter = centerOfMass(feature);
    const zoomLevel = calculateZoomLevel(minLng, maxLng);

    const corners: FlightPoint[] = [
      {
        latitude: featureCenter.geometry.coordinates[1],
        longitude: featureCenter.geometry.coordinates[0],
        zoom: zoomLevel,
        properties: feature.properties,
        bbox: [minLng, minLat, maxLng, maxLat],
      },
      {
        latitude: maxLat,
        longitude: minLng,
        zoom: zoomLevel,
        properties: feature.properties,
      },
      {
        latitude: minLat,
        longitude: maxLng,
        zoom: zoomLevel,
        properties: feature.properties,
      },
      {
        latitude: minLat,
        longitude: minLng,
        zoom: zoomLevel,
        properties: feature.properties,
      },
      {
        latitude: maxLat,
        longitude: minLng,
        zoom: zoomLevel,
        properties: feature.properties,
      },
    ];
    flightPath.push(...corners);
  });

  return flightPath;
};

export const initialViewState: InitialViewStateProps = {
  altitude: 1.5,
  bearing: 0,
  latitude: 39.446888593720935,
  longitude: -95.65494071225665,
  maxZoom: 18,
  minPitch: 0,
  maxPitch: 85,
  minZoom: 0,
  pitch: 0,
  zoom: 3.5,
};

const mainView = new MapView({
  id: 'main',
  controller: true,
});

/** How long the headline pulses for when the story reaches a new threat. */
const PULSE_MS = 1700;
/** How long the minimap takes to fly between camera points. */
const FLY_MS = 3000;
/** How long the story dwells on each camera point. */
const STEP_MS = 10000;

export const WowcastPage: React.FC = () => {
  usePeriodicWowDataRefresh();
  const [isPulsing, setIsPulsing] = useState(false);

  const [minimapViewState, setMinimapViewState] = useState(initialViewState);
  const [minimapView, setMinimapView] = useState(
    () =>
      new MapView({
        id: 'minimap',
        x: window.innerWidth - 420,
        y: window.innerHeight - 640,
        width: '400px',
        height: '400px',
        controller: true,
        clear: true,
      })
  );
  const [mainViewState, setMainViewState] = useState(initialViewState);

  const [visualMode, setVisualMode] = useAtom(visualModeAtom);
  const [mapStyle] = useAtom(mapStyleAtom);
  const wowLayers = useWowLayer({
    extruded: visualMode === '3d',
    visible: true,
    // The wowcast broadcast view is WoW-only; its perimeters are the page.
    enabled: true,
  });
  const regionLayer = useRegionLayer({ mode: 'all' });
  const wowPerimeters = useAtomValue(
    loadable(wowChunksSelector({ type: 'perimeter', enabled: true }))
  );
  const wowPerimetersData = wowPerimeters.state === 'hasData' ? wowPerimeters.data : undefined;

  const flightPath = React.useMemo(() => {
    return createFlightPathFromIndividualFeatures(
      wowPerimetersData?.[0] ?? {
        type: 'FeatureCollection' as const,
        features: [],
      }
    );
  }, [wowPerimetersData]);

  const { currentStep, isAnimating, setIsAnimating, goToNextStep, setCurrentStep } =
    useAnimationLogic(flightPath?.length ?? 0, 0);

  const handleSelectStory = React.useCallback(
    (point: FlightPoint) => {
      setCurrentStep(
        flightPath.findIndex(p => p.properties?.threat_id === point.properties?.threat_id)
      );
      setIsAnimating(true); // Optional: Stop animation when a story is selected
    },
    [flightPath, setCurrentStep, setIsAnimating]
  );

  // The threat the broadcast is on. Read straight off the flight path rather
  // than mirrored into state: every writer of that state wrote this same value.
  const currentPoint = flightPath[currentStep];
  const currentFeatureProperties = currentPoint?.properties;

  // A perimeter is five camera points, so the headline only pulses where a step
  // lands on a different threat. Tracked while rendering because the pulse has
  // to start with the frame that shows the new threat, not one after it.
  const threatKey =
    currentFeatureProperties == null
      ? null
      : `${currentFeatureProperties.threat_id}|${currentFeatureProperties.threatlevel}`;
  const [pulsedThreatKey, setPulsedThreatKey] = useState(threatKey);
  if (pulsedThreatKey !== threatKey) {
    setPulsedThreatKey(threatKey);
    setIsPulsing(true);
  }

  React.useEffect(() => {
    if (!isPulsing) return;
    const timeoutId = setTimeout(() => setIsPulsing(false), PULSE_MS);
    return () => clearTimeout(timeoutId);
  }, [isPulsing, pulsedThreatKey]);

  useEffect(() => {
    const handleResize = () => {
      setMinimapView(
        new MapView({
          id: 'minimap',
          x: window.innerWidth - 420,
          y: window.innerHeight - 640,
          width: '400px',
          height: '400px',
          controller: true,
          clear: true,
        })
      );
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // While playing, the minimap flies to each camera point as the story reaches
  // it. Tracked by the point it last flew to: a re-render mid-flight keeps the
  // same point and does not restart the transition, while a flight path rebuilt
  // from refreshed data yields a new point for the same step and is flown to.
  const [flownPoint, setFlownPoint] = useState<FlightPoint | null>(null);
  if (isAnimating && currentPoint != null && flownPoint !== currentPoint) {
    setFlownPoint(currentPoint);
    setMinimapViewState(previous => ({
      ...previous,
      latitude: currentPoint.latitude,
      longitude: currentPoint.longitude,
      zoom: currentPoint.zoom,
      pitch: 0,
      transitionDuration: FLY_MS,
      transitionInterpolator: new FlyToInterpolator(),
    }));
  }

  // The story advances on its own while playing; pausing leaves it where it is.
  React.useEffect(() => {
    if (!isAnimating) return;
    const timeoutId = setTimeout(goToNextStep, STEP_MS);
    return () => clearTimeout(timeoutId);
  }, [isAnimating, currentStep, goToNextStep]);

  const handlePlayPause = React.useCallback(() => {
    setIsAnimating(prevIsAnimating => {
      const newIsAnimating = !prevIsAnimating;
      return newIsAnimating;
    });
  }, [setIsAnimating]);

  useEffect(() => {
    setVisualMode('2d');
  }, [setVisualMode]);

  const rasterStyle = React.useMemo<StyleSpecification | null>(() => {
    const basemap = getBasemap(mapStyle);
    return basemap?.raster ? buildRasterStyle(basemap.raster) : null;
  }, [mapStyle]);

  const [vectorStyle, setVectorStyle] = React.useState<StyleSpecification | null>(null);

  // Vector styles are fetched as JSON (rather than passed as a URL) so
  // fetchBasemapStyle can absolutize the proxied sprite path -- MapLibre
  // rejects relative sprite URLs before transformRequest runs.
  React.useEffect(() => {
    if (rasterStyle != null) return;
    let cancelled = false;
    fetchBasemapStyle(mapStyle)
      .then(style => {
        if (!cancelled) setVectorStyle(style);
      })
      .catch((error: unknown) => {
        console.error('Failed to load basemap style:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [mapStyle, rasterStyle]);

  const basemapStyle = rasterStyle ?? vectorStyle;

  const handleViewStateChange = React.useCallback(
    ({ viewState, viewId }: { viewState: ViewStateProps; viewId: string }) => {
      if (viewId === 'main') {
        setMainViewState(viewState);
      }
      if (viewId === 'minimap') {
        setMinimapViewState(viewState);
      }
    },
    [setMinimapViewState]
  );

  const bboxOutline = () =>
    new GeoJsonLayer({
      id: 'bbox-outline',
      data: flightPath
        .map(p => {
          const bbox = p.bbox;
          if (!bbox) return null;
          const coordinates = [
            [
              [bbox[0], bbox[1]], // bottom left
              [bbox[0], bbox[3]], // top left
              [bbox[2], bbox[3]], // top right
              [bbox[2], bbox[1]], // bottom right
              [bbox[0], bbox[1]], // back to bottom left to close the polygon
            ],
          ];
          return {
            type: 'Feature' as const,
            geometry: { type: 'Polygon' as const, coordinates: coordinates },
            properties: {
              threat_id: p.properties?.threat_id,
              categoryid: p.properties?.categoryid,
            },
          };
        })
        .filter((p): p is NonNullable<typeof p> => p !== null),
      // extensions: [dataFilterExtension],
      // filterRange: [1, 1],
      pickable: false,
      stroked: true,
      filled: false,
      lineWidthScale: 20,
      lineWidthMinPixels: 2,
      getLineColor: ((d: Feature<GeoJSON.Polygon, { categoryid: CategoryID }>) => {
        const color = lookupCategoryColor(d.properties.categoryid);
        if (!color) {
          console.warn('Unknown WoW categoryid:', d.properties.categoryid);
          return [0, 0, 0, 0] as Color;
        }
        return [color[0], color[1], color[2], 255] as Color;
      }) as Accessor<Feature<GeoJSON.Geometry, { categoryid: CategoryID }>, Color>,
      getLineWidth: 20,
      updateTriggers: {
        filterValue: [currentStep],
        getLineColor: [currentStep],
      },
    });

  return (
    <div
      style={{
        width: '100vw',
        height: '100svh',
        backgroundColor: 'rgb(0, 62, 103)',
      }}
    >
      <Box className="full-width-height" sx={{ display: 'flex' }}>
        <Box
          id="wowcast-map-container"
          onContextMenu={suppressMapContextMenu}
          sx={{
            display: 'flex',
            position: 'relative',
            width: 1,
            height: 'calc(100vh - 210px) !important',
            overflow: 'hidden',
            marginTop: 0,
          }}
        >
          <DeckGL
            initialViewState={
              { main: initialViewState, minimap: initialViewState } as {
                [viewId: string]: typeof initialViewState;
              }
            }
            layers={[regionLayer, wowLayers, bboxOutline()]}
            views={[mainView, minimapView]}
            viewState={{ main: mainViewState, minimap: minimapViewState }}
            onViewStateChange={handleViewStateChange}
          >
            {/* @ts-expect-error MapView used as JSX view wrapper in deck.gl v9 */}
            <MapView key="main" id="main">
              <MaplibreMap
                mapStyle={basemapStyle ?? undefined}
                canvasContextAttributes={{ preserveDrawingBuffer: true }}
                attributionControl={{ customAttribution: 'NLT' }}
                transformRequest={transformProxyRequest}
                interactive={false}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    padding: '0.5rem',
                  }}
                >
                  <ToggleLabelsButton initialVisibility="none" />
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50px',
                    right: '8px',
                    zIndex: 1000,
                  }}
                >
                  <IconButton
                    onClick={handlePlayPause}
                    color="primary"
                    sx={{ backgroundColor: 'white', width: 30, height: 30 }}
                    aria-label={isAnimating ? 'Pause' : 'Play'}
                  >
                    {isAnimating ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton>
                </Box>
              </MaplibreMap>
            </MapView>
            {/* @ts-expect-error */}
            <MapView key="minimap" id="minimap">
              {/* deck.gl passes a view's children through as an array, so
                  multi-child views need explicit keys */}
              <Typography
                key="minimap-title"
                sx={{
                  color: 'white',
                  padding: 2,
                  fontSize: '1.50rem',
                  position: 'absolute',
                  top: -50,
                }}
              >
                {currentFeatureProperties?.time_category === 'past'
                  ? 'Observed Alert'
                  : 'Forecasted Alert'}
                {' - '} {currentFeatureProperties?.threat}
              </Typography>
              <MaplibreMap
                key="minimap-map"
                mapStyle={basemapStyle ?? undefined}
                transformRequest={transformProxyRequest}
                canvasContextAttributes={{ preserveDrawingBuffer: true }}
                attributionControl={false}
                interactive={false}
                style={{
                  border: '1px solid white',
                }}
              />
            </MapView>
          </DeckGL>
        </Box>
      </Box>
      <Box
        sx={{
          color: 'white',
          display: 'flex',
          position: 'absolute',
          flexDirection: 'column',
          alignItems: 'flex-end',
          padding: '0.5rem',
          bottom: 214,
          left: 'max(23.4%, 370px)',
          width: '200px',
          zIndex: 1000,
        }}
      >
        <PostLegend selectedMap="wow" />
      </Box>

      <StoryList
        features={(wowPerimetersData?.[0]?.features ?? []) as unknown as FlightPoint[]}
        flightPath={flightPath}
        currentStep={currentStep}
        onSelect={handleSelectStory}
      />
      <NewsCastFooter isPulsing={isPulsing} currentFeatureProperties={currentFeatureProperties} />
    </div>
  );
};

type NewsCastFooterProps = {
  isPulsing: boolean;
  currentFeatureProperties?: GeoJsonProperties;
};

const NewsCastFooter = React.memo((props: NewsCastFooterProps) => {
  const { currentFeatureProperties } = props;
  return (
    <Box
      sx={{
        bottom: 0,
        right: 0,
        zIndex: 1000,
        left: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        color: 'white',
        display: 'flex',
        position: 'relative',
        flexDirection: 'column',
        alignItems: 'flex-end',
        maxHeight: 210,
      }}
      className="news-cast-wrap"
    >
      <div className={`news-cast-container ${props.isPulsing ? 'pulsing-highlight' : ''}`}>
        <Link to="/">
          <TempoLogo
            height={100}
            width={140}
            style={{
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              padding: '0.5rem',
              minHeight: 100,
              minWidth: 120,
            }}
            aria-label="TEMPO"
          />
        </Link>
        {currentFeatureProperties != null && (
          <>
            <div className="news-cast-detail">
              Threat <strong>{currentFeatureProperties?.threat}</strong>
            </div>
            <div className="news-cast-detail">
              Description
              <span>
                <strong>{currentFeatureProperties?.threatdescription}</strong>
              </span>
            </div>
            <div className="news-cast-detail">
              Threshold Exceeded <strong>{currentFeatureProperties?.threatlevel}</strong>
            </div>
            <div className="news-cast-detail">
              Areas Affected
              <strong>{currentFeatureProperties?.state_intersect}</strong>
            </div>
            {currentFeatureProperties?.structures_exposed && (
              <div className="news-cast-detail">
                Structures Exposed
                <strong>{currentFeatureProperties?.structures_exposed.toLocaleString()}</strong>
              </div>
            )}
            {currentFeatureProperties?.residential_struc_exposed && (
              <div className="news-cast-detail">
                Residential Structures Exposed
                <strong>
                  {currentFeatureProperties?.residential_struc_exposed.toLocaleString()}
                </strong>
              </div>
            )}
            <div className="news-cast-detail">
              Last Updated
              <strong>{new Date(currentFeatureProperties?.lastupdatedon).toLocaleString()}</strong>
            </div>
            <div className="news-cast-detail">
              Source
              <strong>{currentFeatureProperties?.datasource}</strong>
            </div>
          </>
        )}

        <div className="news-cast-footer">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              justifyContent: 'center',
              padding: '0.5rem 0.5rem',
              width: '200px',
              textAlign: 'right',
            }}
          >
            <div className="qr-code-background" />
            FEMA Contact:{' '}
            <a
              style={{
                color: 'white',
                textDecoration: 'none',
              }}
              href="mailto:adam.barker@fema.dhs.gov"
            >
              adam.barker@fema.dhs.gov
            </a>
          </Box>
        </div>
      </div>
      <Box
        sx={{
          padding: '0.5rem 1rem',
          width: '100%',
          textAlign: 'left',
          fontSize: '1.4rem',
        }}
      >
        The WoW layer displays areas across the U.S. that are under severe weather conditions or
        other hazards and risks. The layer is displayed alongside POST`s relative population
        exposure rank, a metric used to prioritize disaster response operations based on the
        predicted exposure of vulnerable populations. This web page and the data shown will
        automatically refresh every 15 minutes.
      </Box>
    </Box>
  );
});
